<script setup lang="ts">
import { Form } from '@inertiajs/vue3';
import {
    ArrowRight,
    CircleCheckBig,
    Link2,
    LoaderCircle,
    Mail,
} from '@lucide/vue';
import EarlyAccessSignupController from '@/actions/App/Http/Controllers/EarlyAccessSignupController';
import InputError from '@/components/InputError.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const props = withDefaults(
    defineProps<{
        /** `hero` lays the fields out in a single row, `panel` stacks them. */
        variant?: 'hero' | 'panel';
        /** Field ids must be unique when the form appears twice on one page. */
        idPrefix?: string;
        class?: string;
    }>(),
    {
        variant: 'panel',
        idPrefix: 'early-access',
        class: '',
    },
);
</script>

<template>
    <Form
        v-bind="EarlyAccessSignupController.form()"
        preserve-scroll
        reset-on-success
        class="w-full"
        v-slot="{ errors, processing, wasSuccessful }"
    >
        <div
            v-if="wasSuccessful"
            class="border-secondary/30 bg-secondary/10 flex items-start gap-3 rounded-xl border p-4"
            role="status"
        >
            <CircleCheckBig
                class="text-secondary mt-0.5 size-5 shrink-0"
                aria-hidden="true"
            />
            <div class="text-sm">
                <p class="font-medium">You are on the list.</p>
                <p class="text-muted-foreground">
                    We will email you as soon as early access opens.
                </p>
            </div>
        </div>

        <div
            v-else
            :class="
                cn(
                    'grid gap-3',
                    props.variant === 'hero' &&
                        'sm:grid-cols-[1.4fr_1fr_auto] sm:items-start',
                    props.class,
                )
            "
        >
            <div class="grid gap-2">
                <Label
                    :for="`${props.idPrefix}-website`"
                    :class="cn(props.variant === 'hero' && 'sr-only')"
                >
                    Your website
                </Label>
                <div class="relative">
                    <Link2
                        class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
                        aria-hidden="true"
                    />
                    <Input
                        :id="`${props.idPrefix}-website`"
                        name="website_url"
                        type="text"
                        inputmode="url"
                        autocomplete="url"
                        placeholder="Enter your website URL"
                        class="pl-9"
                    />
                </div>
                <InputError :message="errors.website_url" />
            </div>

            <div class="grid gap-2">
                <Label
                    :for="`${props.idPrefix}-email`"
                    :class="cn(props.variant === 'hero' && 'sr-only')"
                >
                    Email address
                </Label>
                <div class="relative">
                    <Mail
                        class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
                        aria-hidden="true"
                    />
                    <Input
                        :id="`${props.idPrefix}-email`"
                        name="email"
                        type="email"
                        autocomplete="email"
                        placeholder="you@company.com"
                        required
                        class="pl-9"
                    />
                </div>
                <InputError :message="errors.email" />
            </div>

            <Button
                type="submit"
                :disabled="processing"
                :class="cn('h-9', props.variant === 'hero' && 'sm:mt-0')"
            >
                <LoaderCircle
                    v-if="processing"
                    class="size-4 animate-spin"
                    aria-hidden="true"
                />
                <span>Join early access</span>
                <ArrowRight
                    v-if="!processing"
                    class="size-4"
                    aria-hidden="true"
                />
            </Button>
        </div>

        <p class="text-muted-foreground mt-3 text-xs">
            Pre-launch signup. We only email you when your invite is ready.
        </p>
    </Form>
</template>
